import { supabase } from './supabase';
import { GameReview } from '../types';
import { SupabaseClient, User } from '@supabase/supabase-js';
import { Database } from '../types/supabase';
import { XpAction } from './gamification/types';
import { XpActionKey } from './gamification/xpConfig';
import { toast } from 'react-hot-toast';

export interface SystemStatistics {
  total_users: number;
  total_games: number;
  total_ratings: number;
  total_collections: number;
  total_wishlists: number;
  avg_rating: number | null;
  avg_collection_size: number | null;
  most_popular_games: { id: string; name: string; count: number }[];
  most_popular_categories: { category: string; count: number }[];
}

// Interface para o tipo retornado pela consulta de jogos populares
interface PopularGameData {
  game_id: string;
  [key: string]: any; // Para aceitar a chave estrangeira com nome dinâmico
}

/**
 * Busca estatísticas gerais do sistema
 */
export async function getSystemStatistics(): Promise<SystemStatistics> {
  try {
    // Estatísticas de usuários
    const { count: totalUsers, error: userError } = await supabase
      .from('profiles')
      .select('*', { count: 'exact', head: true });

    if (userError) throw userError;

    // Estatísticas de jogos
    const { count: totalGames, error: gameError } = await supabase
      .from('games')
      .select('*', { count: 'exact', head: true });

    if (gameError) throw gameError;

    // Estatísticas de avaliações
    const { count: totalRatings, error: ratingError } = await supabase
      .from('user_games')
      .select('*', { count: 'exact', head: true })
      .not('rating', 'is', null);

    if (ratingError) throw ratingError;

    // Estatísticas de coleções
    const { count: totalCollections, error: collectionError } = await supabase
      .from('user_games')
      .select('*', { count: 'exact', head: true })
      .eq('owned', true);

    if (collectionError) throw collectionError;

    // Estatísticas de wishlists
    const { count: totalWishlists, error: wishlistError } = await supabase
      .from('user_games')
      .select('*', { count: 'exact', head: true })
      .eq('wishlist', true);

    if (wishlistError) throw wishlistError;

    // Avaliação média global
    const { data: avgRatingData, error: avgRatingError } = await supabase
      .from('user_games')
      .select('rating')
      .not('rating', 'is', null);

    if (avgRatingError) throw avgRatingError;

    let avgRating = null;
    if (avgRatingData && avgRatingData.length > 0) {
      const validRatings = avgRatingData
        .map(r => r.rating)
        .filter((r): r is number => r !== null && r !== undefined);
      
      if (validRatings.length > 0) {
        const sum = validRatings.reduce((acc, val) => acc + val, 0);
        avgRating = sum / validRatings.length;
      }
    }

    // Tamanho médio das coleções
    const { data: collectionSizeData, error: collectionSizeError } = await supabase
      .rpc('get_average_collection_size');

    if (collectionSizeError) {
      console.error('Erro ao buscar tamanho médio das coleções:', collectionSizeError);
    }

    const avgCollectionSize = collectionSizeData || null;

    // Jogos mais populares (mais presentes em coleções)
    const { data: popularGamesData, error: popularGamesError } = await supabase
      .from('user_games')
      .select('game_id, games!user_games_game_id_fkey(id, name)')
      .eq('owned', true)
      .limit(5);

    if (popularGamesError) throw popularGamesError;

    // Contagem de jogos por IDs
    const gameCountMap: Record<string, { id: string; name: string; count: number }> = {};
    
    if (popularGamesData) {
      (popularGamesData as PopularGameData[]).forEach(item => {
        const foreignKeyName = 'games!user_games_game_id_fkey';
        if (item.game_id && item[foreignKeyName]) {
          const game = item[foreignKeyName] as any;
          if (!gameCountMap[item.game_id]) {
            gameCountMap[item.game_id] = { id: item.game_id, name: game.name, count: 0 };
          }
          gameCountMap[item.game_id].count += 1;
        }
      });
    }

    const mostPopularGames = Object.values(gameCountMap)
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    // Categorias mais populares
    const { data: gamesWithCategories, error: categoriesError } = await supabase
      .from('games')
      .select('categories')
      .not('categories', 'is', null);

    if (categoriesError) throw categoriesError;

    const categoryCountMap: Record<string, number> = {};
    
    if (gamesWithCategories) {
      gamesWithCategories.forEach(game => {
        if (game.categories && Array.isArray(game.categories)) {
          game.categories.forEach(category => {
            if (category) {
              categoryCountMap[category] = (categoryCountMap[category] || 0) + 1;
            }
          });
        }
      });
    }

    const mostPopularCategories = Object.entries(categoryCountMap)
      .map(([category, count]) => ({ category, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    return {
      total_users: totalUsers || 0,
      total_games: totalGames || 0,
      total_ratings: totalRatings || 0,
      total_collections: totalCollections || 0,
      total_wishlists: totalWishlists || 0,
      avg_rating: avgRating,
      avg_collection_size: avgCollectionSize,
      most_popular_games: mostPopularGames,
      most_popular_categories: mostPopularCategories
    };
  } catch (error) {
    console.error('Erro ao buscar estatísticas do sistema:', error);
    throw error;
  }
}

/**
 * Busca avaliações recentes de jogos
 * @param limit Número máximo de avaliações para retornar (padrão: 10)
 * @returns Lista de avaliações recentes com informações do jogo e usuário
 */
export async function getRecentGameReviews(limit: number = 10): Promise<GameReview[]> {
  try {
    const { data, error } = await supabase
      .from('game_reviews')
      .select(`
        *,
        profiles:user_id(username, avatar_url),
        games:game_id(id, name, slug, thumb_url)
      `)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;

    // Formatar os dados para corresponder ao tipo GameReview
    const formattedReviews = data?.map(review => {
      return {
        ...review,
        username: review.profiles?.username,
        avatar_url: review.profiles?.avatar_url,
        game_name: review.games?.name,
        game_slug: review.games?.slug,
        game_thumb_url: review.games?.thumb_url
      };
    }) || [];

    return formattedReviews as unknown as GameReview[];
  } catch (error) {
    console.error('Erro ao buscar avaliações recentes:', error);
    throw error;
  }
}

// A função updateMarketPolicies foi removida pois tentava modificar RLS policies
// via uma RPC insegura ('execute_sql') que provavelmente não existe.
// Modificações de RLS devem ser feitas via migrações SQL ou painel Supabase.

// Tipos específicos para dados de atualização
type UpdateXpActionData = Partial<Omit<Database['public']['Tables']['xp_actions']['Update'], 'id' | 'action_key' | 'created_at' | 'updated_at'>>;

/**
 * Verifica se um determinado usuário (ou o usuário logado) tem permissão de administrador.
 */
async function checkAdminPermission(supabase: SupabaseClient<Database>, user: User | null): Promise<boolean> {
    if (!user) return false;

    try {
        const { data: profile, error } = await supabase
      .from('profiles')
      .select('is_admin')
      .eq('id', user.id)
      .single();
    
        if (error) {
            console.error("Erro ao buscar perfil para verificar permissão de admin:", error);
            return false;
        }

        return profile?.is_admin === true;
    } catch (err) {
         console.error("Erro inesperado ao verificar permissão de admin:", err);
         return false;
    }
}

// --- Funções de Administração de Gamificação (Ações de XP) ---

/**
 * [Admin] Busca todas as ações de XP configuradas.
 * Requer privilégios de administrador.
 */
export async function getXpActions(supabase: SupabaseClient<Database>): Promise<{ data: XpAction[] | null; error: any }> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!await checkAdminPermission(supabase, user)) {
        return { data: null, error: { message: 'Permissão negada' } };
    }

    const { data, error } = await supabase
        .from('xp_actions')
        .select('*')
        .order('action_key', { ascending: true });

    const typedData = data as XpAction[] | null;
    return { data: typedData, error };
}

/**
 * [Admin] Busca uma ação de XP específica pela sua chave.
 * Requer privilégios de administrador.
 */
export async function getXpActionByKey(supabase: SupabaseClient<Database>, actionKey: XpActionKey): Promise<{ data: XpAction | null; error: any }> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!await checkAdminPermission(supabase, user)) {
         return { data: null, error: { message: 'Permissão negada' } };
     }

    const { data, error } = await supabase
        .from('xp_actions')
        .select('*')
        .eq('action_key', actionKey)
        .single();

     const typedData = data as XpAction | null;
    return { data: typedData, error };
}

/**
 * [Admin] Atualiza os dados de uma ação de XP existente.
 * Requer privilégios de administrador.
 */
export async function updateXpAction(
    supabase: SupabaseClient<Database>,
    actionKey: XpActionKey,
    updateData: UpdateXpActionData
): Promise<{ data: XpAction | null; error: any }> {
     const { data: { user } } = await supabase.auth.getUser();
     if (!await checkAdminPermission(supabase, user)) {
          return { data: null, error: { message: 'Permissão negada' } };
      }

    // Filtrar campos nulos/undefined
    const updates: Partial<Database['public']['Tables']['xp_actions']['Update']> & { updated_at?: string } = Object.entries(updateData)
        .filter(([, value]) => value !== undefined)
        .reduce((obj, [key, value]) => {
             obj[key as keyof Database['public']['Tables']['xp_actions']['Update']] = value as any;
             return obj;
        }, {} as Partial<Database['public']['Tables']['xp_actions']['Update']> & { updated_at?: string });


    if (Object.keys(updates).length === 0) {
        return { data: null, error: { message: 'Nenhum dado válido para atualização fornecido.' } };
    }

    // Adicionar data de atualização
    updates.updated_at = new Date().toISOString();

    const { data, error } = await supabase
        .from('xp_actions')
        .update(updates)
        .eq('action_key', actionKey)
        .select()
        .single();

     const typedData = data as XpAction | null;
    return { data: typedData, error };
}

/**
 * [Admin] Adiciona uma nova ação de XP ao sistema.
 * Note: Implementação comentada pois requer modificação manual do enum XP_ACTIONS.
 */
/*
// type CreateXpActionData = Omit<Database['public']['Tables']['xp_actions']['Insert'], 'id' | 'created_at' | 'updated_at'>;

export async function createXpAction(
    supabase: SupabaseClient<Database>,
    createData: any // Deveria ser CreateXpActionData
): Promise<{ data: XpAction | null; error: any }> {
     const { data: { user } } = await supabase.auth.getUser();
     if (!await checkAdminPermission(supabase, user)) {
          return { data: null, error: { message: 'Permissão negada' } };
      }

    // Validação básica (adaptar se o tipo for definido)
    if (!createData.action_key || !createData.description || createData.xp_value === undefined) {
        return { data: null, error: { message: 'Campos obrigatórios (action_key, description, xp_value) faltando.' } };
    }

    const { data, error } = await supabase
        .from('xp_actions')
        .insert([{ ...createData }]) // Simplificado, ajustar defaults se necessário
        .select()
        .single();

    // ALERTA: Lembre-se de adicionar createData.action_key ao enum XP_ACTIONS em xpConfig.ts!

    const typedData = data as XpAction | null;
    return { data: typedData, error };
}
*/

// --- Outras Funções de Administração (Placeholder) ---

// Adicionar aqui outras funções administrativas conforme necessário
// Ex: Gerenciar badges, ranks, configurações de sistema, etc. 

type BadgeRow = Database['public']['Tables']['badges']['Row'];

// Interface para dados de criação/atualização (pode já existir ou ser ajustada)
type BadgeUpsertData = Partial<Omit<BadgeRow, 'id' | 'created_at' | 'updated_at'>>;

/**
 * Busca todas as badges definidas no sistema.
 * Ordena por nome por padrão.
 */
export async function getAllBadges(): Promise<BadgeRow[]> {
    console.log('[adminService:getAllBadges] Fetching all badges...');
    const { data, error } = await supabase
        .from('badges')
        .select('*')
        .order('name', { ascending: true }); // Ordenar por nome

    if (error) {
        console.error('[adminService:getAllBadges] Error fetching badges:', error);
        toast.error(`Erro ao buscar badges: ${error.message}`);
        return []; // Retorna array vazio em caso de erro
    }

    console.log(`[adminService:getAllBadges] Fetched ${data?.length || 0} badges.`);
    return data || [];
}

/**
 * Cria uma nova badge no banco de dados.
 * @param badgeData - Os dados da nova badge (sem id, created_at, updated_at).
 * @returns A badge criada ou null em caso de erro.
 */
export async function createBadge(badgeData: BadgeUpsertData): Promise<BadgeRow | null> {
    console.log('[adminService:createBadge] Creating badge:', badgeData.name);
    // Remover ID se estiver presente acidentalmente
    const { id, ...dataToInsert } = badgeData as any;

    const { data, error } = await supabase
        .from('badges')
        .insert(dataToInsert)
        .select()
        .single();

    if (error) {
        console.error('[adminService:createBadge] Error creating badge:', error);
        toast.error(`Erro ao criar badge: ${error.message}`);
        return null;
    }

    console.log('[adminService:createBadge] Badge created successfully:', data);
    return data;
}

/**
 * Atualiza uma badge existente no banco de dados.
 * @param badgeId - O ID da badge a ser atualizada.
 * @param badgeData - Os dados a serem atualizados.
 * @returns A badge atualizada ou null em caso de erro.
 */
export async function updateBadge(
  badgeId: string, 
  badgeData: BadgeUpsertData
): Promise<BadgeRow | null> {
    console.log(`[adminService:updateBadge] Updating badge ${badgeId}...`);
    // Remover campos que não devem ser atualizados diretamente
    const { id, created_at, updated_at, ...dataToUpdate } = badgeData as any;
    dataToUpdate.updated_at = new Date().toISOString(); // Garante a atualização do timestamp

    const { data, error } = await supabase
        .from('badges')
        .update(dataToUpdate)
        .eq('id', badgeId)
        .select()
        .single();

    if (error) {
        console.error(`[adminService:updateBadge] Error updating badge ${badgeId}:`, error);
        toast.error(`Erro ao atualizar badge: ${error.message}`);
        return null;
    }

    console.log(`[adminService:updateBadge] Badge ${badgeId} updated successfully:`, data);
    return data;
}

/**
 * Deleta uma badge do banco de dados.
 * @param badgeId - O ID da badge a ser deletada.
 * @returns true se a deleção foi bem-sucedida, false caso contrário.
 */
export async function deleteBadge(badgeId: string): Promise<boolean> {
    console.warn(`[adminService:deleteBadge] Attempting to delete badge ${badgeId}...`);
    const { error } = await supabase
        .from('badges')
        .delete()
        .eq('id', badgeId);

    if (error) {
        console.error(`[adminService:deleteBadge] Error deleting badge ${badgeId}:`, error);
        toast.error(`Erro ao deletar badge: ${error.message}`);
        return false;
    }

    console.log(`[adminService:deleteBadge] Badge ${badgeId} deleted successfully.`);
    toast.success('Badge deletada com sucesso!');
    return true;
}

// --- Fim Funções CRUD para Badges --- 

/**
 * [Admin] Busca usuários por nome de usuário ou email.
 * @param query - Termo de busca.
 * @param limit - Número máximo de resultados (padrão: 10).
 * @returns Lista de perfis de usuários que correspondem à busca.
 */
export async function searchUsers(query: string, limit: number = 10): Promise<Database['public']['Tables']['profiles']['Row'][]> {
    if (!query || query.trim().length < 2) {
        // Evita buscas vazias ou muito curtas para não sobrecarregar
        return [];
    }
    console.log(`[adminService:searchUsers] Searching for users with query: "${query}"`);

    const { data, error } = await supabase
        .from('profiles')
        .select('*') // Usar select('*') para retornar todas as colunas e satisfazer o tipo
        .or(`username.ilike.%${query}%,email.ilike.%${query}%`) // Busca case-insensitive
        .limit(limit);

    if (error) {
        console.error('[adminService:searchUsers] Error searching users:', error);
        toast.error(`Erro ao buscar usuários: ${error.message}`);
        return [];
    }

    console.log(`[adminService:searchUsers] Found ${data?.length || 0} users.`);
    return data || [];
} 